import { useQueries, useQuery } from "@tanstack/react-query";
import React, { createContext, ReactNode, useMemo } from "react";
import { listLobbies } from "../../game/services/listLobbies";
import { Lobby } from "../../game/Types/LobbyTypes";
import { listLobbyTeams } from "../../game/services/lostLobbyTeams";
import { Team } from "../../game/Types/TeamTypes";

export type HistoryContextType = {
  lobbies: Lobby[];
  teamsMap: { [lobbyId: string]: Team[] };
  isLoading: boolean;
};

const HistoryContext = createContext<HistoryContextType>({
  lobbies: [],
  teamsMap: {},
  isLoading: false,
});

interface HistoryContextProviderProps {
  children: ReactNode;
}

const HistoryContextProvider: React.FC<HistoryContextProviderProps> = ({
  children,
}) => {
  const { data: unsortedLobbies, isLoading: loadingLobbies } = useQuery({
    queryKey: ["lobbies"],
    queryFn: async () => {
      const lobbies = (await listLobbies()) ?? [];

      return lobbies;
    },
  });

  const lobbies =
    unsortedLobbies?.sort((a, b) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }) ?? [];

  const teamsQueries = useQueries({
    queries: lobbies.map((lobby) => ({
      queryKey: ["lobbies", lobby.id, "teams"],
      queryFn: async () => {
        if (!lobby.id) throw new Error("No lobby id provided");
        return listLobbyTeams(lobby.id);
      },
      enabled: !!lobby.id,
    })),
  });

  const teamsMap = useMemo(() => {
    const teams = teamsQueries.flatMap((query) => query.data ?? []);

    const teamsMap: {
      [lobbyId: string]: Team[];
    } = {};

    teams.forEach((team) => {
      if (!teamsMap[team.lobbyId]) {
        teamsMap[team.lobbyId] = [];
      }

      teamsMap[team.lobbyId].push(team);
    });

    return teamsMap;
  }, [teamsQueries, lobbies]);

  const isLoading = useMemo(() => {
    return loadingLobbies || teamsQueries.some((query) => query.isLoading);
  }, [loadingLobbies, teamsQueries]);

  return (
    <HistoryContext.Provider value={{ lobbies, teamsMap, isLoading }}>
      {children}
    </HistoryContext.Provider>
  );
};

export { HistoryContext, HistoryContextProvider };
